import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquareText, X, BookmarkPlus, Pencil } from "lucide-react";

const errMsg = (e, fb) => formatApiErrorDetail(e.response?.data?.detail) || fb;
const NAME_TAG = /\{\s*first[ _]?name\s*\}/gi;
const DATE_TAG = /\{\s*appointment[ _]?date\s*\}/gi;
const TIME_TAG = /\{\s*appointment[ _]?time\s*\}/gi;
const PROV_TAG = /\{\s*provider(?:[ _]?name)?\s*\}/gi;
export const fillTags = (text, appt = {}) => {
  const first = (appt.patient_name || "").trim().split(/\s+/)[0] || "";
  const nice = first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : "there";
  const dt = appt.datetime ? new Date(String(appt.datetime).slice(0, 19)) : null;
  const ok = dt && !Number.isNaN(dt.getTime());
  const date = ok ? dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : "your appointment date";
  const time = ok ? dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "your appointment time";
  const prov = (appt.provider || "").trim() || "your provider";
  const named = text.replace(NAME_TAG, nice);
  if (!appt.datetime) return named;
  return named.replace(DATE_TAG, date).replace(TIME_TAG, time).replace(PROV_TAG, prov);
};

export const TemplatePicker = ({ onInsert, disabled, appt }) => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: tpls = [] } = useQuery({ queryKey: ["message-templates"], queryFn: async () => (await api.get("/message-templates")).data });
  const remove = useMutation({
    mutationFn: (id) => api.delete(`/message-templates/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["message-templates"] }); toast.success("Template removed"); },
    onError: (e) => toast.error(errMsg(e, "Could not remove")),
  });
  const [editing, setEditing] = useState(null);
  const update = useMutation({
    mutationFn: (t) => api.put(`/message-templates/${t.id}`, { name: t.name, body: t.body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["message-templates"] }); toast.success("Template updated"); setEditing(null); },
    onError: (e) => toast.error(errMsg(e, "Could not save changes")),
  });

  return (
    <div data-testid="template-picker">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
        <MessageSquareText className="h-3.5 w-3.5" /> Message templates
      </div>
      {tpls.length === 0 ? (
        <p data-testid="template-empty" className="text-xs text-slate-400">No templates yet. Type a message and click "Save as template" to reuse it. Write {"{first name}"}, {"{appointment date}"}, {"{appointment time}"} or {"{provider}"} to fill them in automatically.</p>
      ) : (
        <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
          {tpls.map((t) => (
            <span key={t.id} className="inline-flex items-center rounded-full border border-slate-200 text-xs text-slate-700 hover:border-[#0B1F44] transition-colors">
              <button data-testid={`template-item-${t.id}`} disabled={disabled} onClick={() => onInsert(fillTags(t.body, appt))} title={t.body} className="pl-3 pr-2 py-1">{t.name}</button>
              {(user?.role === "admin" || user?.email === t.created_by) && (
                <button data-testid={`template-edit-${t.id}`} aria-label={`Edit ${t.name}`} disabled={disabled}
                  onClick={() => setEditing({ id: t.id, name: t.name, body: t.body })}
                  className="p-1.5 text-slate-500 hover:text-[#0B1F44]"><Pencil className="h-3 w-3" /></button>
              )}
              {(user?.role === "admin" || user?.email === t.created_by) && (
                <button data-testid={`template-remove-${t.id}`} aria-label={`Remove ${t.name}`} disabled={disabled || remove.isPending}
                  onClick={() => { if (window.confirm(`Remove the "${t.name}" template?`)) remove.mutate(t.id); }}
                  className="p-1.5 pr-2 text-slate-500 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
              )}
            </span>
          ))}
        </div>
      )}
      {editing && (
        <div data-testid="template-edit-form" className="mt-3 border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50">
          <div className="text-xs font-semibold text-slate-600">Edit template</div>
          <Input data-testid="template-edit-name" value={editing.name} maxLength={60} className="h-8 text-sm bg-white"
            onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Template name" />
          <Textarea data-testid="template-edit-body" rows={4} maxLength={1000} value={editing.body} className="text-sm bg-white"
            onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-slate-400">{editing.body.length}/1000 · tags: {"{first name} {appointment date} {appointment time} {provider}"}</span>
            <div className="flex gap-2">
              <Button data-testid="template-edit-cancel" size="sm" variant="ghost" className="h-8" onClick={() => setEditing(null)}>Cancel</Button>
              <Button data-testid="template-edit-save" size="sm" className="h-8 bg-[#0B1F44] hover:bg-[#13306a] text-white"
                disabled={!editing.name.trim() || !editing.body.trim() || update.isPending} onClick={() => update.mutate(editing)}>Save changes</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const SaveTemplate = ({ body, disabled }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const save = useMutation({
    mutationFn: () => api.post("/message-templates", { name, body }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["message-templates"] }); toast.success("Template saved"); setOpen(false); setName(""); },
    onError: (e) => toast.error(errMsg(e, "Could not save template")),
  });
  if (!open) {
    return (
      <button data-testid="template-save-open" disabled={disabled || !body.trim()} onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs font-medium text-[#0B1F44] hover:underline disabled:text-slate-300 disabled:no-underline">
        <BookmarkPlus className="h-3.5 w-3.5" /> Save as template
      </button>
    );
  }
  return (
    <div className="w-full space-y-1">
    <div className="flex items-center gap-2 w-full">
      <Input data-testid="template-save-name" autoFocus value={name} maxLength={60} onChange={(e) => setName(e.target.value)}
        placeholder="Template name, e.g. Aftercare guide" className="h-8 text-sm" />
      <Button data-testid="template-save-confirm" size="sm" className="h-8 bg-[#0B1F44] hover:bg-[#13306a] text-white"
        disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>Save</Button>
      <Button data-testid="template-save-cancel" size="sm" variant="ghost" className="h-8" onClick={() => { setOpen(false); setName(""); }}>Cancel</Button>
    </div>
    <p data-testid="template-name-hint" className="text-[11px] text-slate-500">
      Tip: use <code className="px-1 rounded bg-slate-100">{"{first name}"}</code> <code className="px-1 rounded bg-slate-100">{"{appointment date}"}</code> <code className="px-1 rounded bg-slate-100">{"{appointment time}"}</code> <code className="px-1 rounded bg-slate-100">{"{provider}"}</code> (e.g. "Hi {"{first name}"}, see you {"{appointment date}"} at {"{appointment time}"} with {"{provider}"}") and each patient's details are filled in.
    </p>
    </div>
  );
};
